import { useMsal } from '@azure/msal-react';
import { Checkbox, Input, InvertButton, Popup, PrimaryButton, RadioButton, StatusButton } from 'component-library';
import _ from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppContext } from '../../../../contexts/AppContext';
import { InviteUser, Oem, User } from '../../../../models/ApiModels';
import { InviteOemUser } from '../../../../services/InviteService';
import { GetUser, UpdateUser } from '../../../../services/UserService';
import { ActionType } from '../../../../store/actionTypes';
import Modal from '../../../../utils/Modal';
import useRequest from '../../../../utils/net/useRequest';
import PermissionsGate, { ROLES } from '../../../../utils/PermissionGate';
import RolesOverview from '../RolesOverview';

interface Props {
  users: any;
  close: () => void;
  addUser: () => void;
  edit?: User;
  deleteUser: () => void;
  currentOem?: Oem;
}

export function SettingsCreateUser(props: Props) {
  const { currentOemTenantId, isHumphreeUser } = useContext(AppContext);
  const { accounts } = useMsal();
  const account = accounts[0];
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [openAlternateInviteLinkPopup, setOpenAlternateInviteLinkPopup] = useState(false);
  const [alternateInviteLinkChecked, setAlternateInviteLinkChecked] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [user, setUser] = useState<InviteUser>({
    displayName: '',
    userRoles: '',
    email: '',
    alternateEmail: '',
  });

  const [currentUser = {}] = useRequest<User>(
    () => GetUser(account?.idTokenClaims?.extension_oemUserId as string),
    [account]
  );

  const [editUser, setEditUser] = useState<User>({ ...props.edit });

  const isDisabled = useMemo(
    () =>
      (!hasChanges && props.edit !== undefined) ||
      (props.edit === undefined && (user.displayName === '' || user.email === '')),
    [user.displayName, user.email, hasChanges, props.edit]
  );

  useEffect(() => {
    if (props.edit === undefined || _.isEqual(props.edit, editUser)) setHasChanges(false);
    else {
      setHasChanges(true);
    }
  }, [editUser]);

  function AddUser(alternateEmail?: string) {
    setLoading(true);
    if (alternateEmail != null) {
      user.alternateEmail = alternateEmail;

      InviteOemUser(user)
        .then(() => {
          setLoading(false);
          props.addUser();
          dispatch({
            type: ActionType.SET_SNACKBAR,
            payload: {
              heading: 'An invitation was just sent!',
              status: 'success',
            },
          });
        })
        .catch((err: any) => {
          setLoading(false);
        });
    } else {
      InviteOemUser(user)
        .then(() => {
          setLoading(false);
          props.addUser();
          dispatch({
            type: ActionType.SET_SNACKBAR,
            payload: {
              heading: 'An invitation was just sent!',
              status: 'success',
            },
          });
        })
        .catch((err: any) => {
          setLoading(false);
        });
    }
  }

  async function UpdateUserAsync() {
    if (props.edit?.id) {
      setLoading(true);
      const userToUpdate: User = {
        ...editUser,
        settings: {
          ...(editUser.settings ?? {}),
          measurementSystem: 'imperial',
        },
      };

      UpdateUser(userToUpdate)
        .then(() => {
          setLoading(false);
          props.addUser();
          dispatch({
            type: ActionType.SET_SNACKBAR,
            payload: { heading: 'User has been updated!', status: 'success' },
          });
          props.close();
        })
        .catch(() => setLoading(false));
    }
  }

  const parseRoles = (rolesString?: string): string[] => {
    if (!rolesString) return [];
    return rolesString.split(',').filter(Boolean);
  };

  const getRolesString = (): string => {
    return props.edit ? editUser.roles || '' : user.userRoles;
  };

  const getCurrentRoles = (): string[] => {
    return parseRoles(getRolesString());
  };

  const getMainRole = (): 'admin' | 'editor' | 'viewer' => {
    const roles = getCurrentRoles();
    if (roles.includes('admin')) return 'admin';
    if (roles.includes('editor')) return 'editor';
    return 'viewer';
  };

  const hasAddonRole = (role: string): boolean => {
    return getCurrentRoles().includes(role);
  };

  const setMainRole = (mainRole: 'admin' | 'editor' | 'viewer') => {
    const roles = getCurrentRoles();
    const addonRoles = roles.filter((r) => r !== 'admin' && r !== 'editor');

    const newRoles = mainRole === 'viewer' ? addonRoles : [mainRole, ...addonRoles];
    const rolesString = newRoles.join(',');

    if (!props.edit) {
      setUser({ ...user, userRoles: rolesString });
    } else {
      setEditUser({ ...editUser, roles: rolesString });
    }
  };

  const toggleAddonRole = (addonRole: string) => {
    const roles = getCurrentRoles();
    const newRoles = roles.includes(addonRole) ? roles.filter((r) => r !== addonRole) : [...roles, addonRole];
    const rolesString = newRoles.join(',');

    if (!props.edit) {
      setUser({ ...user, userRoles: rolesString });
    } else {
      setEditUser({ ...editUser, roles: rolesString });
    }
  };

  const isHumphreeEditorChangingHumphreeRoles = useMemo(
    () => isHumphreeUser && currentUser.roles?.includes('editor') && editUser.oemTenantId === currentUser.oemTenantId,
    [isHumphreeUser, currentUser.roles, editUser.oemTenantId, currentUser.oemTenantId]
  );

  return (
    <Modal>
      {!openPopup && (
        <div className='flex h-full w-full flex-col'>
          <div className='flex flex-col items-center w-full gap-16'>
            <div className='flex flex-col items-center'>
              <span className='text-primary-400 prose-heading3'>{props.edit ? 'Edit user' : 'Add new user'}</span>
              <span className='prose-heading4 text-primary-100 uppercase mt-2'>
                {currentOemTenantId === '12345678-1234-1234-1234-123456789000' && !props.edit
                  ? 'Adding humphree user'
                  : ''}
              </span>
              {!props.edit && <span>Invite link will be valid for 2 days.</span>}
            </div>
            <div className='flex flex-wrap gap-10 w-full'>
              <Input
                label='Name'
                size='Medium'
                placeholder='Name'
                value={editUser ? editUser.displayName : user.displayName}
                onChange={(value: string) => {
                  editUser.displayName = value;
                  user.displayName = value;
                  setEditUser({ ...editUser });
                }}
              />
              {!props.edit ? (
                <Input
                  label='Email'
                  size='Large'
                  placeholder='Email'
                  value={editUser ? editUser.email : user.email}
                  onChange={(value: string) => {
                    editUser.email = value;
                    user.email = value;
                    setEditUser({ ...editUser });
                  }}
                />
              ) : (
                <div className='text-gray-60'>
                  <div>Email</div>
                  <div className='py-3'>{editUser.email}</div>
                </div>
              )}
            </div>
            <div className='flex flex-row gap-12 justify-between sm:justify-start w-full'>
              {isHumphreeEditorChangingHumphreeRoles ? (
                <RoleSelection mainRole={getMainRole()} disabled className='text-gray-40' />
              ) : (
                <RoleSelection mainRole={getMainRole()} onRoleChange={setMainRole} className='text-gray-80' />
              )}

              {currentUser.oemTenantId === '12345678-1234-1234-1234-123456789000' && (
                <PermissionsGate roles={[ROLES.admin]} rejected={undefined}>
                  <div className='flex flex-col'>
                    <span className='prose-heading4 text-gray-80 mb-4'>Add-on:</span>
                    <div className='flex flex-col gap-2'>
                      <div className='flex gap-3'>
                        <Checkbox
                          onClick={() => toggleAddonRole('presetCertified')}
                          active={hasAddonRole('presetCertified')}
                        />
                        <span className='text-gray-80 prose-paragraphBase'>Preset Certified</span>
                      </div>
                      <PermissionsGate roles={[ROLES.softwareManager]} rejected={undefined}>
                        <div className='flex gap-3'>
                          <Checkbox
                            onClick={() => toggleAddonRole('softwareManager')}
                            active={hasAddonRole('softwareManager')}
                          />
                          <span className='text-gray-80 prose-paragraphBase'>Software Manager</span>
                        </div>
                      </PermissionsGate>
                    </div>
                  </div>
                </PermissionsGate>
              )}
            </div>
            <RolesOverview />
            {!props.edit && (
              <div className='flex gap-2 w-full items-center'>
                <Checkbox
                  active={alternateInviteLinkChecked}
                  onClick={() => {
                    setAlternateInviteLinkChecked(!alternateInviteLinkChecked);
                  }}
                />
                <span>Send invite link to {currentUser.email} instead</span>
              </div>
            )}
          </div>
        </div>
      )}

      {!loading && (
        <div className='w-full flex justify-between items-end h-full pt-10'>
          <div className='flex flex-row gap-4 items-center'>
            <InvertButton
              onClick={() => {
                if (hasChanges) setOpenPopup(true);
                props.close();
              }}
              label='Close'
              size='small'
            />
            <PermissionsGate roles={[ROLES.admin]} rejected={undefined}>
              {props.edit && (
                <StatusButton label='Delete user' status='error' size='small' onClick={() => props.deleteUser()} />
              )}
            </PermissionsGate>
          </div>
          <PrimaryButton
            onClick={async () => {
              if (props.edit) {
                await UpdateUserAsync();
              } else if (alternateInviteLinkChecked) {
                setOpenAlternateInviteLinkPopup(true);
              } else AddUser();
            }}
            disabled={isDisabled}
            label={props.edit ? 'Save user' : 'Add user'}
            size='small'
          />
        </div>
      )}
      {openAlternateInviteLinkPopup && (
        <Popup
          close={() => {
            setAlternateInviteLinkChecked(false);
            setOpenAlternateInviteLinkPopup(false);
          }}
          confirm={async () => {
            await AddUser(currentUser.email);
            setOpenAlternateInviteLinkPopup(false);
            setOpenPopup(false);
          }}
          heading='Are you sure you want to send a new invite link to yourself?'
          paragraph='You will have to send the link to the customer manually.'
          status='primary'
          cancelText='Cancel'
          confirmText='Send new invite'
        />
      )}
      {loading && (
        <div className='w-full flex justify-center items-center h-full flex-col gap-3'>
          <span className='prose-heading4 text-primary-400'>{props.edit ? 'Updating user...' : 'Adding user...'}</span>
          <div className='w-12 h-12 border-l-2 border-primary-400 rounded-full animate-spin'></div>
        </div>
      )}
    </Modal>
  );
}

interface RoleSelectionProps {
  mainRole: 'admin' | 'editor' | 'viewer';
  onRoleChange?: (role: 'admin' | 'editor' | 'viewer') => void;
  disabled?: boolean;
  className?: string;
}

const RoleSelection = ({ mainRole, onRoleChange, disabled, className }: RoleSelectionProps) => {
  const roles = [
    { id: 'admin', label: 'Admin' },
    { id: 'editor', label: 'Editor' },
    { id: 'viewer', label: 'View only' },
  ] as const;

  return (
    <div className='flex flex-col min-w-max'>
      <span className={`prose-heading4 ${className} mb-4`}>Roles:</span>
      <div className='flex flex-col gap-2'>
        {roles.map((role) => (
          <div key={role.id} className='flex gap-3'>
            <RadioButton onClick={() => onRoleChange?.(role.id)} active={mainRole === role.id} disabled={disabled} />
            <span className={`${className} prose-paragraphBase`}>{role.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
